import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Search, ArrowBack } from "@mui/icons-material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getSystemMemoryUtilisationGraphTable } from "../../redux/actions";

export default function CpuUsageLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortDirection, setSortDirection] = useState("asc");
  const [durationFilter, setDurationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    dispatch(
      getSystemMemoryUtilisationGraphTable({
        duration: durationFilter,
        status: statusFilter,
      })
    );
  }, [dispatch, durationFilter, statusFilter]);

  const { data, loading } = useSelector(
    (state) => state.dashboard.getcpuTableGraph || { data: {}, loading: false }
  );

  const cpuUsageData = data?.cpu_graph || [];

  const sortedData = [...cpuUsageData].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "High":
        return "#D32F2F";
      case "Moderate":
        return "#F57C00";
      case "Normal":
        return "#388E3C";
      default:
        return "#000";
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const latestTimestamp = Math.max(
    ...cpuUsageData.map((row) => row.date * 1000)
  );

  const filteredData = cpuUsageData
    .filter((row) => {
      const rowTime = row.date * 1000;

      let matchesDuration = true;
      if (durationFilter === "Last 1 Hour") {
        matchesDuration = rowTime >= latestTimestamp - 1 * 60 * 60 * 1000;
      } else if (durationFilter === "Last 6 Hours") {
        matchesDuration = rowTime >= latestTimestamp - 6 * 60 * 60 * 1000;
      } else if (durationFilter === "Last 12 Hours") {
        matchesDuration = rowTime >= latestTimestamp - 12 * 60 * 60 * 1000;
      } else if (durationFilter === "Last 24 Hours") {
        matchesDuration = rowTime >= latestTimestamp - 24 * 60 * 60 * 1000;
      }

      const matchesStatus =
        !statusFilter ||
        row.status?.toLowerCase() === statusFilter.toLowerCase();

      const matchesCpuSearch = row.cpu_utilisation
        ?.toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      return matchesDuration && matchesStatus && matchesCpuSearch;
    })

    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
    });

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: 4, px: 5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              height: "40px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "14px",
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Duration</InputLabel>
              <Select
                value={durationFilter}
                onChange={(e) => setDurationFilter(e.target.value)}
                label="Duration"
              >
                <MenuItem value="Last 1 Hour">Last 1 Hour</MenuItem>
                <MenuItem value="Last 6 Hours">Last 6 Hours</MenuItem>
                <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Moderate">Moderate</MenuItem>
                <MenuItem value="Normal">Normal</MenuItem>
              </Select>
            </FormControl>

            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "270px",
                height: "42px",
                "& .MuiOutlinedInput-input": {
                  padding: "10px 14px",
                },
              }}
              InputProps={{
                startAdornment: <Search sx={{ color: "#888", mr: 1 }} />,
              }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#7B2D43",
              color: "#7B2D43",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#7B2D43",
                backgroundColor: "transparent",
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Box>

        <Typography variant="h6" fontWeight={600} mb={2}>
          CPU Usage
        </Typography>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #ddd",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#D0C5CA" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box display="flex" alignItems="center">
                    Date & Time
                    <Box
                      display="flex"
                      flexDirection="column"
                      sx={{ ml: 1, display: "inline-flex" }}
                    >
                      <Box
                        onClick={() => setSortDirection("asc")}
                        sx={{
                          background: "rgb(225, 225, 225)",
                          border: "1px solid #ccc",
                          borderRadius: "2px",
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          mb: "1px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ArrowDropUpIcon
                          sx={{
                            fontSize: 16,
                            color: sortDirection === "asc" ? "#7B2D43" : "#999",
                          }}
                        />
                      </Box>

                      <Box
                        onClick={() => setSortDirection("desc")}
                        sx={{
                          background: "rgb(225, 225, 225)",
                          border: "1px solid #ccc",
                          borderRadius: "2px",
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ArrowDropDownIcon
                          sx={{
                            fontSize: 16,
                            color:
                              sortDirection === "desc" ? "#7B2D43" : "#999",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell sx={{ fontWeight: 600 }}>Server/Component</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CPU Usage %</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Time period</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{ "& td": { py: 2, borderBottom: "1px solid #ddd" } }}
                  >
                    <TableCell>
                      {new Date(row.date * 1000).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </TableCell>
                    <TableCell>{row.server_name}</TableCell>
                    <TableCell>{row.cpu_utilisation}%</TableCell>
                    <TableCell
                      sx={{
                        color: getStatusColor(row.status),
                        fontWeight: 600,
                      }}
                    >
                      {row.status}
                    </TableCell>
                    <TableCell>{row.time_period}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}
